import React, { useState, useEffect } from 'react';
import {
  createTerritory, listTerritories, createDivision, listDivisions,
  createArea, listAreas, deactivateTerritory, deactivateDivision, deactivateArea
} from '../services/orgStructureService';
import './OrgStructure.css';

function OrgStructure({ user }) {
  const [territories, setTerritories] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const [newTerritory, setNewTerritory] = useState('');
  const [newDivision, setNewDivision] = useState({ name: '', territoryId: '' });
  const [newArea, setNewArea] = useState({ name: '', divisionId: '' });

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [t, d, a] = await Promise.all([listTerritories(), listDivisions(), listAreas()]);
      setTerritories(t);
      setDivisions(d);
      setAreas(a);
    } catch (err) {
      console.error('Error loading org structure:', err);
    } finally {
      setLoading(false);
    }
  };

  const activeTerritories = territories.filter(t => t.active);
  const activeDivisions = divisions.filter(d => d.active);

  const handleAddTerritory = async () => {
    if (!newTerritory.trim()) return;
    setBusy(true); setError('');
    try {
      await createTerritory({ name: newTerritory, createdBy: user });
      setNewTerritory('');
      await loadAll();
    } catch (err) {
      console.error(err);
      setError('Could not create territory.');
    } finally { setBusy(false); }
  };

  const handleAddDivision = async () => {
    const territory = territories.find(t => t.id === newDivision.territoryId);
    if (!newDivision.name.trim() || !territory) { setError('Pick a territory and enter a division name.'); return; }
    setBusy(true); setError('');
    try {
      await createDivision({ name: newDivision.name, territory, createdBy: user });
      setNewDivision({ name: '', territoryId: '' });
      await loadAll();
    } catch (err) {
      console.error(err);
      setError('Could not create division.');
    } finally { setBusy(false); }
  };

  const handleAddArea = async () => {
    const division = divisions.find(d => d.id === newArea.divisionId);
    if (!newArea.name.trim() || !division) { setError('Pick a division and enter an area name.'); return; }
    setBusy(true); setError('');
    try {
      await createArea({ name: newArea.name, division, createdBy: user });
      setNewArea({ name: '', divisionId: '' });
      await loadAll();
    } catch (err) {
      console.error(err);
      setError('Could not create area.');
    } finally { setBusy(false); }
  };

  const handleDeactivate = async (type, id, label) => {
    if (!window.confirm(`Deactivate "${label}"? Anything still linked to it stays as-is, but it won't be selectable going forward.`)) return;
    try {
      if (type === 'territory') await deactivateTerritory(id);
      if (type === 'division') await deactivateDivision(id);
      if (type === 'area') await deactivateArea(id);
      await loadAll();
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div className="org">
      <div className="org-header">
        <h2>🗺️ Organizational Structure</h2>
      </div>

      {error && <div className="org-error">⚠️ {error}</div>}

      {loading ? (
        <div className="org-empty">Loading...</div>
      ) : (
        <div className="org-columns">

          {/* ── TERRITORIES ── */}
          <div className="org-column">
            <h3>Territories</h3>
            <div className="org-add-row">
              <input className="org-input" placeholder="New territory name"
                value={newTerritory} onChange={e => setNewTerritory(e.target.value)} />
              <button className="org-btn" onClick={handleAddTerritory} disabled={busy}>+</button>
            </div>
            <div className="org-list">
              {activeTerritories.length === 0 ? (
                <div className="org-empty-small">No territories yet</div>
              ) : activeTerritories.map(t => (
                <div key={t.id} className="org-item">
                  <span>{t.name}</span>
                  <button className="org-remove" onClick={() => handleDeactivate('territory', t.id, t.name)}>✕</button>
                </div>
              ))}
            </div>
          </div>

          {/* ── DIVISIONS ── */}
          <div className="org-column">
            <h3>Divisions</h3>
            <div className="org-add-row">
              <select className="org-input" value={newDivision.territoryId}
                onChange={e => setNewDivision({ ...newDivision, territoryId: e.target.value })}>
                <option value="">Territory...</option>
                {activeTerritories.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
              <input className="org-input" placeholder="New division name"
                value={newDivision.name} onChange={e => setNewDivision({ ...newDivision, name: e.target.value })} />
              <button className="org-btn" onClick={handleAddDivision} disabled={busy}>+</button>
            </div>
            <div className="org-list">
              {activeDivisions.length === 0 ? (
                <div className="org-empty-small">No divisions yet</div>
              ) : activeDivisions.map(d => (
                <div key={d.id} className="org-item">
                  <span>{d.name} <span className="org-parent">({d.territoryName})</span></span>
                  <button className="org-remove" onClick={() => handleDeactivate('division', d.id, d.name)}>✕</button>
                </div>
              ))}
            </div>
          </div>

          {/* ── AREAS ── */}
          <div className="org-column">
            <h3>Areas</h3>
            <div className="org-add-row">
              <select className="org-input" value={newArea.divisionId}
                onChange={e => setNewArea({ ...newArea, divisionId: e.target.value })}>
                <option value="">Division...</option>
                {activeDivisions.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
              <input className="org-input" placeholder="New area name"
                value={newArea.name} onChange={e => setNewArea({ ...newArea, name: e.target.value })} />
              <button className="org-btn" onClick={handleAddArea} disabled={busy}>+</button>
            </div>
            <div className="org-list">
              {areas.filter(a => a.active).length === 0 ? (
                <div className="org-empty-small">No areas yet</div>
              ) : areas.filter(a => a.active).map(a => (
                <div key={a.id} className="org-item">
                  <span>{a.name} <span className="org-parent">({a.divisionName})</span></span>
                  <button className="org-remove" onClick={() => handleDeactivate('area', a.id, a.name)}>✕</button>
                </div>
              ))}
            </div>
          </div>

        </div>
      )}

      <div className="org-note">
      </div>
    </div>
  );
}

export default OrgStructure;
